// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/User/user/Search';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = { 'Access-Control-Allow-Origin': '*' };

// ============= Types =============

export interface SearchUsersProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  searchValue: string;
}

export interface UserSearchResult {
  companyName?: string;
  country?: string;
  department?: string;
  firstName?: string;
  id: string;
  isOwner?: boolean;
  jobTitle?: string;
  lastName?: string;
  name?: string;
  office?: string;
  preferredName?: string;
  proNoun?: string;
  title?: string;
  username?: string;
}

export interface UserSearchResponse {
  data: {
    items: UserSearchResult[];
  };
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Searches for users by email or ID
 * @param getAccessToken Function to acquire access token
 * @param searchValue Search string to filter users
 * @returns Promise with API response containing user data or error
 */
export const searchUsers = async ({
  getAccessToken,
  searchValue,
}: SearchUsersProps): Promise<UserSearchResult[]> => {
  try {
    console.log('🔄 Searching users...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${encodeURIComponent(searchValue)}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Users searched successfully`);

    return data;
  } catch (error) {
    console.error('🔴 Error searching users:', error);
    throw error;
  }
};
